import React, { useEffect, useState, useRef } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Button from "aldoo-ra/Button"
import { showNotification } from "aldoo-ra/Notification"
import { GetAgents, UpdateTicketAssignment } from "./api"

export default function AssignDialog({
  isOpen,
  ticketId,
  ticketLabel,
  currentAssignee,
  onClose,
  onAssigned,
}) {
  const [agents, setAgents] = useState([])
  const [selectedAgent, setSelectedAgent] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      // Reset state when dialog closes
      setSelectedAgent(null)
      return
    }

    async function loadAgents() {
      if (agents.length > 0) {
        // If agents are already loaded, set the selected agent based on currentAssignee
        if (currentAssignee) {
          const assigned = agents.find((a) => a.value === currentAssignee)
          if (assigned) {
            setSelectedAgent(assigned)
          }
        }
        return
      }

      setLoading(true)
      try {
        const res = await GetAgents()
        if (res.result) {
          const agentOptions = res.data.map((a) => ({
            value: a.id,
            label: a.name || a.email, // Use email as fallback if name is missing
          }))
          setAgents(agentOptions)

          // Set the selected agent based on currentAssignee
          if (currentAssignee) {
            const assigned = agentOptions.find(
              (a) => a.value === currentAssignee
            )
            if (assigned) {
              setSelectedAgent(assigned)
            }
          }
        }
      } catch (error) {
        showNotification({
          message: "Failed to load agents",
          className: "bg-admin_error text-white",
        })
      } finally {
        setLoading(false)
      }
    }

    loadAgents()
  }, [isOpen, agents.length, currentAssignee])

  const handleAssign = async () => {
    if (!selectedAgent) return

    setLoading(true)
    try {
      await UpdateTicketAssignment(ticketId, selectedAgent.value)
      showNotification({
        message: `Ticket assigned to ${selectedAgent.label}`,
        className: "bg-admin_primary text-admin_text_inv",
      })
      onAssigned()
    } catch (error) {
      showNotification({
        message: "Assignment failed",
        className: "bg-admin_error text-white",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleUnassign = async () => {
    setLoading(true)
    try {
      await UpdateTicketAssignment(ticketId, null)
      showNotification({
        message: "Ticket unassigned",
        className: "bg-admin_warning text-admin_text_inv",
      })
      onAssigned()
    } catch (error) {
      showNotification({
        message: "Unassignment failed",
        className: "bg-admin_error text-white",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin_centered"
    >
      <div>
        <Typography variant="admin_h4" className="mb-8">
          Assign Ticket: {ticketLabel}
        </Typography>

        <div className="space-y-6">
          <div>
            <Typography 
              variant="admin_body_accent" 
              className="mb-2"
            >
              Select Agent:
            </Typography>

            <div className="relative">
              <DropdownSelect
                variant="admin"
                options={agents}
                value={selectedAgent ? [selectedAgent] : []}
                onChange={(vals) => setSelectedAgent(vals[0])}
                className="w-full"
                searchEnabled
                disabled={loading}
                zIndex={9999}
              />
            </div>
          </div>

          <div className="flex justify-between gap-4 mt-8">
            <Button
              variant="admin_secondary"
              onClick={handleUnassign}
              disabled={loading}
            >
              Unassign
            </Button>

            <Button
              variant="admin_primary"
              onClick={handleAssign}
              disabled={!selectedAgent || loading}
            >
              Assign
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}